import { useState, useEffect, useCallback } from "react";
import { tagsApi } from "@/api/social";

export function TagsSection({ companyId }: { companyId: string }) {
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tagsApi.list(companyId);
      setTags(res.tags || []);
    } catch (e) {
      console.error("[Tags] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const addTag = async () => {
    const trimmed = newTag.trim().toLowerCase();
    if (!trimmed || tags.includes(trimmed)) return;
    setSaving(true);
    try {
      const res = await tagsApi.add(companyId, [...tags, trimmed]);
      setTags(res.tags || []);
      setNewTag("");
    } catch (e) {
      console.error("[Tags] add failed:", e);
    } finally {
      setSaving(false);
    }
  };

  const removeTag = async (tag: string) => {
    const updated = tags.filter((t) => t !== tag);
    setSaving(true);
    try {
      const res = await tagsApi.add(companyId, updated);
      setTags(res.tags || []);
    } catch (e) {
      console.error("[Tags] remove failed:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-zinc-200">Tags</h3>
        {loading && <span className="text-xs text-zinc-500">Loading...</span>}
        {saving && <span className="text-xs text-zinc-500">Saving...</span>}
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-zinc-800 px-3 py-1 text-xs font-medium text-zinc-300"
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="text-zinc-500 hover:text-red-400"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTag()}
          placeholder="Add tag..."
          className="flex-1 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:border-orange-500 focus:outline-none"
        />
        <button
          onClick={addTag}
          disabled={!newTag.trim() || saving}
          className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}
